import { useCallback, useEffect, useRef, useState } from 'react';
import { CopyDayRequest } from '../types';
import { timeEntriesAPI } from '../services/api';
import { assertAuthAccount, getCurrentAuthAccount } from '../services/authAccount';
import {
  acknowledgeCopyDay, copyDayCommand, copyDayProblemMessage, definiteCopyDayProblem, readCopyDayOperations,
  saveCopyDayOperation, StoredCopyDayOperation, validateCopyDayResult,
} from '../services/copyDayOperation';
import { isCanonicalDate } from '../utils/calendarDate';

const uncertainMessage = 'The copy outcome is unknown. Check its status or retry this operation with the same dates. It may already have completed.';
const errorMessage = (error: unknown) => error instanceof Error ? error.message : uncertainMessage;

export const useCopyDayOperations = (accountKey: string | null, refreshDashboard: () => Promise<boolean>) => {
  const [operations, setOperations] = useState<StoredCopyDayOperation[]>([]);
  const [busyIds, setBusyIds] = useState<string[]>([]);
  const [error, setError] = useState('');
  const locks = useRef(new Set<string>());
  const starting = useRef(false);
  const refreshRef = useRef(refreshDashboard);
  refreshRef.current = refreshDashboard;
  const currentAccount = useRef(accountKey);
  currentAccount.current = accountKey;
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const update = useCallback((op: StoredCopyDayOperation) => {
    if (!mounted.current || currentAccount.current !== op.accountKey) return;
    setOperations(previous => [...previous.filter(item => item.accountKey === op.accountKey && item.operationId !== op.operationId), op]);
  }, []);

  const persist = useCallback((op: StoredCopyDayOperation) => {
    try {
      const saved = saveCopyDayOperation(op);
      update(saved);
      return saved;
    } catch (failure) {
      // A confirmed terminal response remains known even if saving it fails.
      update({ ...op, message: errorMessage(failure) });
      throw failure;
    }
  }, [update]);

  const lock = useCallback((id: string) => {
    if (locks.current.has(id)) return false;
    locks.current.add(id);
    if (mounted.current) setBusyIds(Array.from(locks.current));
    return true;
  }, []);
  const unlock = useCallback((id: string) => {
    locks.current.delete(id);
    if (mounted.current) setBusyIds(Array.from(locks.current));
  }, []);

  const refreshCopied = useCallback(async (op: StoredCopyDayOperation) => {
    if (op.state !== 'copied') return;
    try {
      assertAuthAccount(op.accountKey);
      // Persist known success BEFORE any read; failure here never offers another POST.
      persist({ ...op, needsRefresh: true, message: undefined });
      const refreshed = await refreshRef.current();
      if (!refreshed) throw new Error('Dashboard read failed');
      persist({ ...op, needsRefresh: false, message: undefined });
    } catch (failure) {
      const message = getCurrentAuthAccount() !== op.accountKey
        ? 'Copy succeeded. Sign in to the original account to refresh its Dashboard.'
        : 'Copy succeeded, but Dashboard refresh failed or recovery storage could not be updated. Retry Dashboard refresh; no additional entries will be copied.';
      try { persist({ ...op, needsRefresh: true, message }); } catch { /* Known success remains in memory and the original descriptor remains durable. */ }
    }
  }, [persist]);

  const settle = useCallback(async (op: StoredCopyDayOperation, value: unknown) => {
    const result = validateCopyDayResult(value, op);
    if (!result) return false;
    const terminal: StoredCopyDayOperation = { ...op, state: result.outcome, result,
      problem: undefined, needsRefresh: result.outcome === 'copied', message: result.outcome === 'rejected' ? copyDayProblemMessage(result.error) : undefined };
    try { persist(terminal); } catch { return true; }
    if (result.outcome === 'copied' && currentAccount.current === op.accountKey && getCurrentAuthAccount() === op.accountKey) await refreshCopied(terminal);
    return true;
  }, [persist, refreshCopied]);

  const submitOperation = useCallback(async (op: StoredCopyDayOperation, firstSubmission = false) => {
    if (op.state !== 'uncertain' || !lock(op.operationId)) return;
    try {
      assertAuthAccount(op.accountKey);
      const saved = persist({ ...op, message: undefined });
      if (saved.state !== 'uncertain') return;
      const result = await timeEntriesAPI.copyDay(copyDayCommand(saved), saved.accountKey);
      if (!(await settle(saved, result))) persist({ ...saved, message: uncertainMessage });
    } catch (failure) {
      const candidate = definiteCopyDayProblem(failure, op);
      // Rejecting this replay before acceptance cannot settle an earlier ambiguous send.
      const problem = candidate?.mutationOutcome === 'notSubmitted' && !firstSubmission ? null : candidate;
      const next: StoredCopyDayOperation = problem
        ? { ...op, state: problem.mutationOutcome === 'notSubmitted' ? 'notSubmitted' : 'rejected', problem, message: copyDayProblemMessage(problem) }
        : { ...op, message: getCurrentAuthAccount() !== op.accountKey ? errorMessage(failure) : uncertainMessage };
      try { persist(next); } catch { /* Fail closed; immutable original descriptor is retained. */ }
    } finally {
      unlock(op.operationId);
    }
  }, [lock, persist, settle, unlock]);

  const reconcile = useCallback(async (op: StoredCopyDayOperation) => {
    if (op.state !== 'uncertain' || !lock(op.operationId)) return;
    try {
      const value = await timeEntriesAPI.getCopyDayOperation(op.operationId, op.accountKey);
      if (!(await settle(op, value))) persist({ ...op, message: uncertainMessage });
    } catch {
      // Missing/pending/unavailable status cannot prove that an earlier POST will not commit.
      try { persist({ ...op, message: uncertainMessage }); } catch { /* Keep recovery visible. */ }
    } finally { unlock(op.operationId); }
  }, [lock, persist, settle, unlock]);

  const retryRefresh = useCallback(async (op: StoredCopyDayOperation) => {
    if (!lock(op.operationId)) return;
    try { await refreshCopied(op); } finally { unlock(op.operationId); }
  }, [lock, refreshCopied, unlock]);

  useEffect(() => {
    setError('');
    setOperations([]);
    if (!accountKey) return;
    const reload = () => {
      try { setOperations(readCopyDayOperations(accountKey)); } catch (failure) { setError(errorMessage(failure)); }
    };
    try {
      const stored = readCopyDayOperations(accountKey);
      setOperations(stored);
      stored.forEach(op => {
        if (op.state === 'uncertain') void reconcile(op);
        else if (op.state === 'copied' && op.needsRefresh) void retryRefresh(op);
      });
    } catch (failure) { setError(errorMessage(failure)); }
    window.addEventListener('storage', reload);
    return () => window.removeEventListener('storage', reload);
  }, [accountKey, reconcile, retryRefresh]);

  const start = async (sourceDate: string, destinationDate: string, previous?: StoredCopyDayOperation): Promise<string | null> => {
    if (starting.current) return null;
    starting.current = true;
    setError('');
    try {
      if (!accountKey) throw new Error('Sign in again to establish a stable account identity before copying a day.');
      assertAuthAccount(accountKey);
      if (!isCanonicalDate(sourceDate) || !isCanonicalDate(destinationDate) || sourceDate === destinationDate) throw new Error('Choose a valid destination date different from the source.');
      const stored = readCopyDayOperations(accountKey);
      if (stored.some(op => op.state === 'uncertain' || op.needsRefresh)) throw new Error('Review and recover the existing copy before starting a new operation.');
      // Terminal receipts that need no refresh are informational banners. They
      // cannot redirect or block a separately confirmed copy of the clicked day.
      if (previous) acknowledgeCopyDay(previous);
      if (typeof crypto === 'undefined' || typeof crypto.randomUUID !== 'function') throw new Error('Secure operation identity is unavailable. Open this app over HTTPS and try again.');
      const command: CopyDayRequest = { operationId: crypto.randomUUID(), sourceDate, destinationDate };
      const op: StoredCopyDayOperation = { ...command, accountKey, contractVersion: 1, state: 'uncertain' };
      // A failed first persistence has not submitted anything; keep the editable form.
      saveCopyDayOperation(op);
      if (previous) setOperations(items => items.filter(item => item.operationId !== previous.operationId));
      update(op);
      // Return the ID immediately so the dialog locks its dates while the POST runs.
      void submitOperation(op, true);
      return op.operationId;
    } catch (failure) {
      setError(errorMessage(failure));
      return null;
    } finally { starting.current = false; }
  };

  const acknowledge = (op: StoredCopyDayOperation) => {
    try {
      assertAuthAccount(op.accountKey);
      acknowledgeCopyDay(op);
      setOperations(previous => previous.filter(item => item.operationId !== op.operationId));
      return true;
    } catch (failure) { setError(errorMessage(failure)); return false; }
  };

  return { operations: operations.filter(op => op.accountKey === accountKey), busyIds, error, start,
    retry: submitOperation, reconcile, retryRefresh, acknowledge };
};
